import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, User, Star, ShoppingBag, MessageSquare, ShieldCheck, Store, ExternalLink, Clock, Eye, CalendarCheck, Settings, Plus, Briefcase, Pause, Play, Tag, UserPlus, UserCheck, Users, Check, X, Gift } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import ProfileCapabilities from '../components/ProfileCapabilities';
import ProfilePortfolio from '../components/ProfilePortfolio';
import AccountingDocuments from '../components/AccountingDocuments';
import FriendsSection from '../components/FriendsSection';
import { usePresence } from '../hooks/usePresence';
import { ReputationBadge, BadgeChips, TrustedSellerBadge } from '../components/ReputationBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Unified public profile (seller + buyer in one) — mounted at /lietotajs/:userId.
// /pro/:userId and /buyer/:userId redirect here. Combines identity, SELLER
// reputation/reviews (from /api/pro/:id), BUYER reputation/reviews (from
// /api/user-profile/:id), public links (shop / business pages / classifieds)
// and owner-only management.
export default function UnifiedProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewOrderId, setReviewOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sellerOrders, setSellerOrders] = useState([]);
  const isOwner = !!(user && user._id === userId);
  const [ownerBiz, setOwnerBiz] = useState(null);
  const [bizPages, setBizPages] = useState([]);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [previewPublic, setPreviewPublic] = useState(false);
  const [clsCount, setClsCount] = useState(0);
  const [friendStatus, setFriendStatus] = useState('none');
  const [friends, setFriends] = useState([]);
  const [friendBusy, setFriendBusy] = useState(false);
  const friendsOnline = usePresence(friends.map((f) => f.id));
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  // Referral code this guest arrived with (URL ?ref= or captured earlier).
  const refCode = (() => {
    try {
      return new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('kolv_ref') || '';
    } catch (e) { return ''; }
  })();
  const [inviterName, setInviterName] = useState('');
  useEffect(() => {
    if (user || !refCode) { setInviterName(''); return; }
    axios.get(`${API}/referrals/resolve/${refCode}`)
      .then(({ data }) => setInviterName(data.name || ''))
      .catch(() => setInviterName(''));
  }, [user, refCode]);

  // Public friends list (everyone) + my relationship status (logged-in visitors).
  useEffect(() => {
    axios.get(`${API}/friends/of/${userId}`).then(({ data }) => setFriends(data.friends || [])).catch(() => setFriends([]));
    if (user && user._id !== userId) {
      axios.get(`${API}/friends/status/${userId}`).then(({ data }) => setFriendStatus(data.status || 'none')).catch(() => setFriendStatus('none'));
    } else {
      setFriendStatus('self');
    }
  }, [userId, user]);

  const friendAction = async (kind) => {
    setFriendBusy(true);
    try {
      let res;
      if (kind === 'request') res = await axios.post(`${API}/friends/request/${userId}`, {});
      else if (kind === 'accept') res = await axios.post(`${API}/friends/${userId}/accept`, {});
      else if (kind === 'decline') res = await axios.post(`${API}/friends/${userId}/decline`, {});
      else if (kind === 'remove') res = await axios.delete(`${API}/friends/${userId}`);
      const st = res?.data?.status || 'none';
      setFriendStatus(st);
      if (st === 'friends') {
        toast.success(tr('Tagad esat draugi!', 'You are now friends!', 'Теперь вы друзья!'));
        axios.get(`${API}/friends/of/${userId}`).then(({ data }) => setFriends(data.friends || [])).catch(() => {});
      } else if (kind === 'request' && st === 'pending_sent') {
        toast.success(tr('Uzaicinājums nosūtīts', 'Request sent', 'Заявка отправлена'));
      }
    } catch (e) {
      toast.error(formatApiError(e, tr('Neizdevās', 'Failed', 'Ошибка')));
    } finally { setFriendBusy(false); }
  };

  useEffect(() => {
    if (isOwner) axios.get(`${API}/business-page/me`).then(({ data }) => setOwnerBiz(data)).catch(() => {});
  }, [isOwner]);

  useEffect(() => {
    if (isOwner) axios.get(`${API}/classifieds/mine`).then(({ data }) => setClsCount(data.active_count || 0)).catch(() => {});
  }, [isOwner]);

  useEffect(() => {
    axios.get(`${API}/business-page/${userId}/pages`).then(({ data }) => setBizPages(data.pages || [])).catch(() => setBizPages([]));
  }, [userId]);

  const refetchProfile = async () => {
    try { const { data } = await axios.get(`${API}/user-profile/${userId}`); setProfile(data); } catch (e) { /* ignore */ }
  };
  const toggleShopPause = async () => {
    const next = !(profile?.shop?.is_paused);
    setPauseBusy(true);
    try { await axios.put(`${API}/shop-profile/pause`, { paused: next }); await refetchProfile(); toast.success(next ? 'Veikals pauzēts' : 'Veikals aktīvs'); }
    catch (e) { toast.error('Neizdevās'); } finally { setPauseBusy(false); }
  };
  const toggleBizPause = async () => {
    const next = !(ownerBiz?.is_paused);
    setPauseBusy(true);
    try { await axios.put(`${API}/business-page/me/pause`, { paused: next }); setOwnerBiz((b) => ({ ...b, is_paused: next })); toast.success(next ? 'Biznesa lapa pauzēta' : 'Biznesa lapa aktīva'); }
    catch (e) { toast.error('Neizdevās'); } finally { setPauseBusy(false); }
  };

  const [pagePauseBusy, setPagePauseBusy] = useState(null);
  const togglePagePause = async (pg) => {
    const next = !pg.is_paused;
    setPagePauseBusy(pg.id);
    try {
      await axios.put(`${API}/business-page/me/pages/${pg.id}/pause`, { paused: next });
      setBizPages((prev) => prev.map((x) => (x.id === pg.id ? { ...x, is_paused: next } : x)));
      toast.success(next ? 'Lapa pauzēta' : 'Lapa aktīva');
    } catch (e) { toast.error('Neizdevās'); } finally { setPagePauseBusy(null); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/user-profile/${userId}`);
        setProfile(data);
        // Seller side (reputation + seller reviews) for the unified profile.
        try { const pr = await axios.get(`${API}/pro/${userId}`); setPro(pr.data); } catch (e) { /* no seller profile */ }
        // If current user is a seller, fetch orders with this buyer
        if (user) {
          try {
            const ordersRes = await axios.get(`${API}/orders`);
            const buyerOrders = (ordersRes.data || []).filter(o =>
              o.user_id === userId && o.payment_status === 'paid'
            );
            setSellerOrders(buyerOrders);
          } catch (e) { /* no orders endpoint or not a seller */ }
        }
      } catch (e) { navigate('/'); }
      finally { setLoading(false); }
    };
    load();
  }, [userId, user, navigate]);

  const handleSubmitReview = async () => {
    if (!reviewText.trim() || !reviewOrderId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/buyer-reviews`, {
        buyer_id: userId,
        order_id: reviewOrderId,
        rating: reviewRating,
        comment: reviewText
      });
      setReviewText('');
      setReviewOrderId('');
      // Refresh
      const { data } = await axios.get(`${API}/user-profile/${userId}`);
      setProfile(data);
    } catch (e) {
      alert(formatApiError(e, 'Kļūda'));
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
    </div>
  );

  if (!profile) return null;
  const { user: profileUser, buyer_reviews, buyer_avg_rating, buyer_total_reviews, total_orders, trusted_buyer, shop, business } = profile;
  const displayName = profileUser.public_name || profileUser.name;
  const fmtDM = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`; };
  const pausedLabel = (until) => {
    const dm = fmtDM(until);
    if (language === 'lv') return dm ? `Pauzēts līdz ${dm}` : 'Pauzēts';
    if (language === 'ru') return dm ? `Приостановлен до ${dm}` : 'Приостановлен';
    return dm ? `Paused until ${dm}` : 'Paused';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="unified-profile-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6">
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'lv' ? 'Atpakaļ' : 'Back'}</span>
        </button>

        {/* Guest arrived via a referral link — invite them to join */}
        {!user && refCode && (
          <div className="mb-6 rounded-2xl bg-gradient-to-br from-[#D97736] to-[#C0562A] text-white px-5 py-4 flex items-center justify-between gap-4 flex-wrap shadow-sm" data-testid="referral-join-banner">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <Gift className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-outfit font-semibold">
                  {inviterName
                    ? tr(`${inviterName} aicina tevi pievienoties KoLV!`, `${inviterName} invites you to join KoLV!`, `${inviterName} приглашает вас в KoLV!`)
                    : tr('Pievienojies KoLV!', 'Join KoLV!', 'Присоединяйтесь к KoLV!')}
                </div>
                <div className="text-sm text-white/85">
                  {tr('Pērc un pārdod droši visā Latvijā — reģistrējies dažās sekundēs.', 'Buy and sell safely across Latvia — sign up in seconds.', 'Покупайте и продавайте по всей Латвии — регистрация за секунды.')}
                </div>
              </div>
            </div>
            <Button onClick={() => navigate('/register')} className="bg-white text-[#D97736] hover:bg-white/90 font-medium shrink-0" data-testid="referral-join-btn">
              {tr('Reģistrēties', 'Sign up', 'Регистрация')}
            </Button>
          </div>
        )}

        {/* Owner: toggle a preview of the page exactly as other users see it */}
        {isOwner && (
          previewPublic ? (
            <div className="flex items-center justify-between gap-3 flex-wrap mb-6 rounded-2xl bg-[#121212] text-white px-5 py-3" data-testid="public-preview-bar">
              <span className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#D97736]" />
                {language === 'lv' ? 'Priekšskatījums — tā šo lapu redz citi lietotāji' : language === 'ru' ? 'Предпросмотр — так вашу страницу видят другие' : 'Preview — this is how other users see your page'}
              </span>
              <button onClick={() => setPreviewPublic(false)} className="text-sm font-semibold px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="exit-public-preview-btn">
                {language === 'lv' ? 'Iziet no priekšskatījuma' : language === 'ru' ? 'Выйти из предпросмотра' : 'Exit preview'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setPreviewPublic(true)}
              className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[#E5E4E0] bg-white text-sm font-medium text-[#121212] hover:border-[#D97736] transition-colors"
              data-testid="view-as-public-btn"
            >
              <Eye className="w-4 h-4 text-[#D97736]" />
              {language === 'lv' ? 'Skatīt manu publisko profilu (kā to redz citi)' : language === 'ru' ? 'Посмотреть мой публичный профиль (как видят другие)' : 'View my public profile (as others see it)'}
            </button>
          )
        )}

        {/* Profile Card */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-8 mb-6" data-testid="buyer-card">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-[#F3F2EE] flex items-center justify-center text-3xl font-bold text-[#D97736]">
              {displayName?.charAt(0)?.toUpperCase() || <User className="w-8 h-8" />}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-outfit text-2xl font-bold text-[#121212]">{displayName}</h1>
                {trusted_buyer && (
                  <Badge className="bg-green-100 text-green-800 border-green-200 gap-1" data-testid="trusted-buyer-badge">
                    <ShieldCheck className="w-3 h-3" />
                    {language === 'lv' ? 'Uzticams pircējs' : 'Trusted buyer'}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {buyer_total_reviews > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-[#D97736] text-[#D97736]" />
                    <span className="font-semibold text-[#121212]">{buyer_avg_rating}</span>
                    <span className="text-sm text-[#5C5C5C]">({buyer_total_reviews})</span>
                  </div>
                )}
                <Badge variant="outline" className="text-xs">
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  {total_orders} {language === 'lv' ? 'pasūtījumi' : 'orders'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Friend invitation — logged-in visitors viewing someone else's profile */}
          {user && !isOwner && (
            <div className="mt-6 pt-5 border-t border-[#F3F2EE] flex items-center gap-2 flex-wrap" data-testid="friend-action-row">
              {friendStatus === 'friends' ? (
                <>
                  <Button onClick={() => navigate(`/messages?to=${userId}&name=${encodeURIComponent(displayName || '')}`)} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="message-friend-btn">
                    <MessageSquare className="w-4 h-4 mr-1" /> {tr('Rakstīt ziņu', 'Message', 'Написать')}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={friendBusy} className="border-[#4A6B53] text-[#4A6B53] hover:bg-[#EEF4EF]" data-testid="friend-status-btn">
                        <UserCheck className="w-4 h-4 mr-1" /> {tr('Draugi', 'Friends', 'Друзья')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="remove-friend-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{tr('Noņemt no draugiem?', 'Remove friend?', 'Удалить из друзей?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {tr(
                            `Vai tiešām noņemt ${displayName || 'šo lietotāju'} no draugiem? Draudzība tiks pārtraukta abiem.`,
                            `Are you sure you want to remove ${displayName || 'this user'} from friends? The friendship ends for both of you.`,
                            `Удалить ${displayName || 'этого пользователя'} из друзей? Дружба прекратится у обоих.`
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="remove-friend-cancel">{tr('Atcelt', 'Cancel', 'Отмена')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => friendAction('remove')} className="bg-[#C0392B] hover:bg-[#A93226] text-white" data-testid="remove-friend-confirm">
                          {tr('Noņemt', 'Remove', 'Удалить')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : friendStatus === 'pending_sent' ? (
                <Button variant="outline" disabled className="border-[#E5E4E0] text-[#9CA3AF]" data-testid="friend-status-btn">
                  <Clock className="w-4 h-4 mr-1" /> {tr('Uzaicinājums nosūtīts', 'Request sent', 'Заявка отправлена')}
                </Button>
              ) : friendStatus === 'pending_received' ? (
                <>
                  <Button onClick={() => friendAction('accept')} disabled={friendBusy} className="bg-[#4A6B53] hover:bg-[#3d5945] text-white" data-testid="accept-friend-btn">
                    <Check className="w-4 h-4 mr-1" /> {tr('Pieņemt draudzību', 'Accept', 'Принять')}
                  </Button>
                  <Button variant="outline" onClick={() => friendAction('decline')} disabled={friendBusy} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="decline-friend-btn">
                    <X className="w-4 h-4 mr-1" /> {tr('Noraidīt', 'Decline', 'Отклонить')}
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => friendAction('request')} disabled={friendBusy} className="border-[#D97736] text-[#D97736] hover:bg-[#FFF6F0]" data-testid="add-friend-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> {tr('Uzaicināt draugos', 'Add friend', 'Добавить в друзья')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* ─── Friends list (public) ─── */}
        {friends.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="friends-section">
            <h2 className="font-outfit text-lg font-semibold text-[#121212] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D97736]" /> {tr('Draugi', 'Friends', 'Друзья')} ({friends.length})
            </h2>
            <div className="flex flex-wrap gap-4 mt-4">
              {friends.map((f) => (
                <button key={f.id} onClick={() => navigate(`/lietotajs/${f.id}`)} className="flex flex-col items-center gap-1 w-16 group" data-testid={`friend-${f.id}`}>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-[#4A6B53] flex items-center justify-center text-white font-semibold overflow-hidden">
                      {f.avatar_url ? <img src={f.avatar_url} alt={f.name} className="w-full h-full object-cover" /> : (f.name?.charAt(0)?.toUpperCase() || '?')}
                    </div>
                    {friendsOnline[f.id] && (
                      <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white" title={tr('Tiešsaistē', 'Online', 'Онлайн')} data-testid={`friend-online-${f.id}`} />
                    )}
                  </div>
                  <span className="text-xs text-[#5C5C5C] truncate w-full text-center group-hover:text-[#D97736]">{f.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── Seller reputation (unified): rating, level, badges, Trusted seller ─── */}
        {pro?.reputation && (pro.reputation.review_count > 0 || pro.reputation.completed_gigs > 0 || pro.reputation.trusted_seller) && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="unified-seller-reputation">
            <h2 className="font-outfit font-semibold text-[#121212] mb-3">
              {language === 'lv' ? 'Pārdevēja reputācija' : language === 'ru' ? 'Репутация продавца' : 'Seller reputation'}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <ReputationBadge level={pro.reputation.level} levelInfo={pro.reputation.level_info} avgRating={pro.reputation.avg_rating} reviewCount={pro.reputation.review_count} />
              {pro.reputation.trusted_seller && <TrustedSellerBadge />}
            </div>
            {pro.reputation.badges?.length > 0 && <div className="mt-3"><BadgeChips badges={pro.reputation.badges} /></div>}
            <div className="flex gap-8 mt-4 pt-4 border-t border-[#F3F2EE]">
              <div>
                <p className="text-2xl font-bold text-[#121212] font-outfit">{pro.reputation.completed_gigs || 0}</p>
                <p className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Pabeigti darbi' : language === 'ru' ? 'Завершено' : 'Completed gigs'}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#121212] font-outfit">{pro.reputation.review_count || 0}</p>
                <p className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Atsauksmes' : language === 'ru' ? 'Отзывы' : 'Reviews'}</p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Classifieds card (C2C): add + manage own private listings ─── */}
        {isOwner && !previewPublic && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="profile-classifieds-link">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-[#E6F2F0] flex items-center justify-center flex-shrink-0"><Tag className="w-6 h-6 text-[#2F7E79]" /></div>
                <div className="min-w-0">
                  <h3 className="font-outfit font-semibold text-[#121212]">{language === 'lv' ? 'Privātie sludinājumi' : language === 'ru' ? 'Частные объявления' : 'Classifieds'}</h3>
                  <p className="text-sm text-[#5C5C5C]">{clsCount} {language === 'lv' ? 'aktīvi sludinājumi' : language === 'ru' ? 'активных объявлений' : 'active listings'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button onClick={() => navigate('/sludinajumi/jauns')} className="bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2" data-testid="add-classified-profile-btn">
                  <Plus className="w-4 h-4" /> {language === 'lv' ? 'Pievienot sludinājumu' : language === 'ru' ? 'Добавить' : 'Add listing'}
                </Button>
                <Button variant="outline" onClick={() => navigate('/mani-sludinajumi')} className="border-[#E5E4E0] text-[#2F7E79] gap-2" data-testid="manage-classifieds-btn">
                  <Settings className="w-4 h-4" /> {language === 'lv' ? 'Pārvaldīt' : language === 'ru' ? 'Управлять' : 'Manage'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Shop card: visit + manage + pause (owner) · create if missing ─── */}
        {(shop || (isOwner && !previewPublic)) && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="profile-shop-link">
            {(shop || profileUser?.is_seller) ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-[#EEF4EF] flex items-center justify-center flex-shrink-0">
                      <Store className="w-6 h-6 text-[#4A6B53]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-outfit font-semibold text-[#121212] truncate">{shop?.shop_name || (language === 'lv' ? 'Mans veikals' : language === 'ru' ? 'Мой магазин' : 'My shop')}</h3>
                        {shop?.is_verified && (
                          <Badge className="bg-green-100 text-green-800 border-green-200 gap-1 text-xs">
                            <ShieldCheck className="w-3 h-3" />
                            {language === 'lv' ? 'Verificēts' : language === 'ru' ? 'Проверен' : 'Verified'}
                          </Badge>
                        )}
                        {shop?.is_paused && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1 text-xs" data-testid="shop-paused-badge">
                            <Clock className="w-3 h-3" />
                            {pausedLabel(shop.pause_until)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#5C5C5C]">
                        {shop ? `${shop.listing_count} ${language === 'lv' ? 'aktīvi sludinājumi' : language === 'ru' ? 'активных объявлений' : 'active listings'}` : (language === 'lv' ? 'Tavs veikals' : language === 'ru' ? 'Ваш магазин' : 'Your shop')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    {(shop?.user_id || profileUser?.id) && (
                      <Button onClick={() => navigate(`/shop/${shop?.user_id || profileUser.id}`)}
                        className="bg-[#4A6B53] hover:bg-[#3C5844] text-white gap-2" data-testid="visit-shop-btn">
                        <Store className="w-4 h-4" />
                        {language === 'lv' ? 'Apmeklēt veikalu' : language === 'ru' ? 'Посетить магазин' : 'Visit shop'}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isOwner && !previewPublic && (
                      <Button variant="outline" onClick={() => navigate('/shop-profile')}
                        className="border-[#E5E4E0] text-[#4A6B53] gap-2" data-testid="manage-shop-btn">
                        <Settings className="w-4 h-4" />
                        {language === 'lv' ? 'Pārvaldīt' : language === 'ru' ? 'Управлять' : 'Manage'}
                      </Button>
                    )}
                  </div>
                </div>
                {isOwner && !previewPublic && (
                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#F3F2EE]" data-testid="owner-shop-pause">
                    <span className="text-sm text-[#5C5C5C] flex items-center gap-2"><Clock className="w-4 h-4" /> {language === 'lv' ? 'Pauzēt veikalu' : language === 'ru' ? 'Приостановить магазин' : 'Pause shop'}</span>
                    <Switch checked={!!shop?.is_paused} onCheckedChange={toggleShopPause} disabled={pauseBusy} data-testid="owner-shop-pause-toggle" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="create-shop-cta">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#EEF4EF] flex items-center justify-center flex-shrink-0"><Store className="w-6 h-6 text-[#4A6B53]" /></div>
                  <div>
                    <h3 className="font-outfit font-semibold text-[#121212]">{language === 'lv' ? 'Atvērt veikalu' : language === 'ru' ? 'Открыть магазин' : 'Open a shop'}</h3>
                    <p className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Sāc pārdot preces vai pakalpojumus' : language === 'ru' ? 'Начните продавать' : 'Start selling'}</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/shop-onboarding')} className="bg-[#4A6B53] hover:bg-[#3C5844] text-white gap-2 flex-shrink-0" data-testid="create-shop-btn">
                  <Plus className="w-4 h-4" /> {language === 'lv' ? 'Izveido veikalu' : language === 'ru' ? 'Создать магазин' : 'Create shop'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ─── Unified business pages card: header (manage/pause) + all pages in one style ─── */}
        {(bizPages.length > 0 || (isOwner && !previewPublic)) && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="profile-business-link">
            {bizPages.length > 0 ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-[#FFF6F0] flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-6 h-6 text-[#D97736]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-outfit font-semibold text-[#121212]">{language === 'lv' ? 'Uzņēmuma lapas' : language === 'ru' ? 'Страницы бизнеса' : 'Business pages'}</h3>
                      <p className="text-sm text-[#5C5C5C]">{language === 'lv' ? `${bizPages.length} aktīva lapa(s)` : language === 'ru' ? `${bizPages.length} активных стр.` : `${bizPages.length} active page(s)`}</p>
                    </div>
                  </div>
                  {isOwner && !previewPublic && (
                    <Button variant="outline" onClick={() => navigate('/business-templates')}
                      className="border-[#E5E4E0] text-[#D97736] gap-2" data-testid="manage-business-btn">
                      <Settings className="w-4 h-4" />
                      {language === 'lv' ? 'Pārvaldīt' : language === 'ru' ? 'Управлять' : 'Manage'}
                    </Button>
                  )}
                </div>

                {/* Page rows — unified style */}
                <div className="space-y-3">
                  {bizPages.map((pg) => {
                    const KIND_LABELS = {
                      general: { lv: 'Rezervācijas & Pasākumi', ru: 'Бронирование и события', en: 'Reservations & Events' },
                      salon: { lv: 'Skaistumkopšana & Salons', ru: 'Красота и салон', en: 'Beauty & Salon' },
                      health: { lv: 'Veselība & Klīnika', ru: 'Здоровье и клиника', en: 'Health & Clinic' },
                      fitness: { lv: 'Sports & Fitness', ru: 'Спорт и фитнес', en: 'Sports & Fitness' },
                      auto: { lv: 'Auto serviss', ru: 'Автосервис', en: 'Auto service' },
                      education: { lv: 'Izglītība & Kursi', ru: 'Образование и курсы', en: 'Education & Courses' },
                      stay: { lv: 'Naktsmītne', ru: 'Проживание', en: 'Stay' },
                      trade: { lv: 'Pakalpojumi & Amatnieks', ru: 'Услуги и мастер', en: 'Services & Trade' },
                      realestate: { lv: 'Nekustamais īpašums', ru: 'Недвижимость', en: 'Real estate' },
                    };
                    const L = (o) => o[language] || o.lv;
                    const tLabel = pg.template === 'menu'
                      ? L({ lv: 'Digitālā ēdienkarte', ru: 'Меню', en: 'Menu' })
                      : pg.template === 'business_card'
                        ? L({ lv: 'Vizītkarte & Info', ru: 'Визитка', en: 'Business card' })
                        : L(KIND_LABELS[pg.kind] || KIND_LABELS.general);
                    const title = pg.display_name || tLabel;
                    const secondary = [pg.display_name ? tLabel : null, pg.address].filter(Boolean).join(' · ');
                    const showStats = isOwner && !previewPublic;
                    return (
                      <div key={pg.id || pg.order} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border border-[#F3F2EE] bg-[#FAFAF8]" data-testid={`business-page-row-${pg.id}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-12 h-12 rounded-xl bg-[#FFF6F0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {pg.logo ? <img src={pg.logo} alt={title} className="w-full h-full object-cover" /> : <Briefcase className="w-6 h-6 text-[#D97736]" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-outfit font-semibold text-[#121212] truncate">{title}</p>
                              {pg.is_paused && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1 text-xs"><Clock className="w-3 h-3" />{language === 'lv' ? 'Pauzēts' : language === 'ru' ? 'Пауза' : 'Paused'}</Badge>
                              )}
                            </div>
                            {secondary && <p className="text-xs text-[#5C5C5C] truncate mt-0.5">{secondary}</p>}
                            {showStats && (
                              <div className="flex items-center gap-3 mt-1.5 text-xs text-[#8A8A8A]">
                                <span className="flex items-center gap-1" data-testid={`page-views-${pg.id}`}><Eye className="w-3.5 h-3.5" /> {pg.views || 0} {language === 'lv' ? 'skatījumi' : language === 'ru' ? 'просм.' : 'views'}</span>
                                <span className="flex items-center gap-1" data-testid={`page-bookings-${pg.id}`}><CalendarCheck className="w-3.5 h-3.5" /> {pg.bookings || 0} {language === 'lv' ? 'rezervācijas' : language === 'ru' ? 'брони' : 'bookings'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap flex-shrink-0 w-full sm:w-auto">
                          <Button onClick={() => navigate(pg.id ? `/uznemums/${userId}/${pg.id}` : `/uznemums/${userId}`)}
                            variant="outline" className="border-[#E5E4E0] text-[#D97736] gap-2 flex-1 sm:flex-none" data-testid={`visit-business-page-${pg.id}`}>
                            <ExternalLink className="w-4 h-4" />
                            {language === 'lv' ? 'Apmeklēt' : language === 'ru' ? 'Открыть' : 'Visit'}
                          </Button>
                          {showStats && (
                            <>
                              <Button onClick={() => navigate(`/business-templates?page=${pg.id}`)}
                                variant="outline" className="border-[#E5E4E0] text-[#121212] gap-2 flex-1 sm:flex-none" data-testid={`manage-page-row-${pg.id}`}>
                                <Settings className="w-4 h-4" />
                                {language === 'lv' ? 'Pārvaldīt' : language === 'ru' ? 'Управлять' : 'Manage'}
                              </Button>
                              <Button onClick={() => togglePagePause(pg)} disabled={pagePauseBusy === pg.id}
                                variant="outline" className={`gap-2 flex-1 sm:flex-none ${pg.is_paused ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'}`} data-testid={`pause-page-row-${pg.id}`}>
                                {pg.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                {pg.is_paused ? (language === 'lv' ? 'Aktivizēt' : language === 'ru' ? 'Активировать' : 'Activate') : (language === 'lv' ? 'Pauzēt' : language === 'ru' ? 'Пауза' : 'Pause')}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Global pause toggle (owner) */}
                {isOwner && !previewPublic && (
                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#F3F2EE]" data-testid="owner-biz-pause">
                    <span className="text-sm text-[#5C5C5C] flex items-center gap-2"><Clock className="w-4 h-4" /> {language === 'lv' ? 'Pauzēt visas lapas' : language === 'ru' ? 'Приостановить все страницы' : 'Pause all pages'}</span>
                    <Switch checked={!!ownerBiz?.is_paused} onCheckedChange={toggleBizPause} disabled={pauseBusy} data-testid="owner-biz-pause-toggle" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="create-business-cta">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#FFF6F0] flex items-center justify-center flex-shrink-0"><Briefcase className="w-6 h-6 text-[#D97736]" /></div>
                  <div>
                    <h3 className="font-outfit font-semibold text-[#121212]">{language === 'lv' ? 'Izveidot uzņēmuma lapu' : language === 'ru' ? 'Создать бизнес-страницу' : 'Create business page'}</h3>
                    <p className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Rezervācijas, izvēlne, info' : language === 'ru' ? 'Бронирование, меню, инфо' : 'Reservations, menu, info'}</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/business-templates')} className="bg-[#D97736] hover:bg-[#C06528] text-white gap-2 flex-shrink-0" data-testid="create-business-btn">
                  <Plus className="w-4 h-4" /> {language === 'lv' ? 'Izveidot' : language === 'ru' ? 'Создать' : 'Create'}
                </Button>
              </div>
            )}
          </div>
        )}


        {/* ═══ OWNER-ONLY management (moved from private profile) ═══ */}
        {isOwner && !previewPublic && (
          <div className="space-y-6 mb-6" data-testid="owner-management">
            {/* Roles / capabilities + portfolio */}
            <ProfileCapabilities />
            <ProfilePortfolio />

            {/* Grāmatvedība ar cilnēm (Veikals / Biznesa lapa / Mani pirkumi) */}
            <AccountingDocuments />

            {/* Draugi — uzaicinājumi + mani draugi */}
            <FriendsSection />
          </div>
        )}

        {/* Reviews received as a SELLER (from unified reputation) */}
        {pro?.reviews?.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden mb-6" data-testid="unified-seller-reviews">
            <div className="px-6 py-4 border-b border-[#E5E4E0]">
              <h2 className="font-outfit font-semibold text-lg text-[#121212]">
                {language === 'lv' ? 'Atsauksmes par pārdevēju' : language === 'ru' ? 'Отзывы о продавце' : 'Reviews as a seller'} ({pro.reviews.length})
              </h2>
            </div>
            <div className="divide-y divide-[#E5E4E0]">
              {pro.reviews.map((r) => (
                <div key={r.id} className="px-6 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm text-[#121212]">{r.reviewer_name}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-[#5C5C5C]">{new Date(r.created_at).toLocaleDateString('lv-LV')}</span>
                  </div>
                  <p className="text-sm text-[#5C5C5C]">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews received as a BUYER */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden mb-6" data-testid="unified-buyer-reviews">
          <div className="px-6 py-4 border-b border-[#E5E4E0]">
            <h2 className="font-outfit font-semibold text-lg text-[#121212]">
              {language === 'lv' ? 'Atsauksmes par pircēju' : language === 'ru' ? 'Отзывы о покупателе' : 'Reviews as a buyer'}
            </h2>
          </div>
          {buyer_reviews?.length > 0 ? (
            <div className="divide-y divide-[#E5E4E0]">
              {buyer_reviews.map((r) => (
                <div key={r.id} className="px-6 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm text-[#121212]">{r.reviewer_name}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-[#5C5C5C]">
                      {new Date(r.created_at).toLocaleDateString('lv-LV')}
                    </span>
                  </div>
                  <p className="text-sm text-[#5C5C5C]">{r.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-[#5C5C5C] opacity-20" />
              <p className="text-[#5C5C5C] text-sm">{language === 'lv' ? 'Nav atsauksmju' : 'No reviews yet'}</p>
            </div>
          )}
        </div>

        {/* Write Review (only for sellers who have orders with this buyer) */}
        {user && user._id !== userId && sellerOrders.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
            <h3 className="font-outfit font-semibold text-[#121212] mb-4">
              {language === 'lv' ? 'Atstāt atsauksmi par pircēju' : 'Leave a review for buyer'}
            </h3>
            <div className="space-y-3">
              <select
                value={reviewOrderId}
                onChange={(e) => setReviewOrderId(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] text-sm"
                data-testid="review-order-select"
              >
                <option value="">{language === 'lv' ? 'Izvēlieties pasūtījumu' : 'Select order'}</option>
                {sellerOrders.map(o => (
                  <option key={o.id || o._id} value={o.id || o._id}>
                    #{(o.id || o._id)?.slice(-6)} — €{o.total} ({new Date(o.created_at).toLocaleDateString('lv-LV')})
                  </option>
                ))}
              </select>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setReviewRating(s)} type="button">
                    <Star className={`w-6 h-6 ${s <= reviewRating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                  </button>
                ))}
              </div>
              <Textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder={language === 'lv' ? 'Jūsu atsauksme...' : 'Your review...'}
                className="bg-[#FAFAFA] border-[#E5E4E0]"
                data-testid="buyer-review-text"
              />
              <Button
                onClick={handleSubmitReview}
                disabled={submitting || !reviewText.trim() || !reviewOrderId}
                className="bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="submit-buyer-review"
              >
                {submitting ? '...' : (language === 'lv' ? 'Iesniegt atsauksmi' : 'Submit Review')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
